package fyi.moko.hotpl.domain.usecase

import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceRichness
import fyi.moko.hotpl.domain.model.RichnessSignal
import fyi.moko.hotpl.domain.model.Visit

/** Visits at which a place stops reading as a one-off and starts reading as a habit. */
const val REGULAR_VISIT_THRESHOLD = 3

/**
 * Derives the [PlaceRichness] of a place from its own fields plus its visit history.
 *
 * Pure and deterministic — no clock, no I/O — so the "화면이 언제 얼마나 자라는가" rule
 * can be judged in isolation before any UI depends on it. Same spirit as
 * [RevisitWindowCalculator]: the rule lives in domain, the phrasing stays in the UI.
 *
 * ── 신호 선정 근거 ──────────────────────────────
 *  · `liked`는 축으로 쓰지 않는다. [Place.liked]와 PlaceFormViewModel 모두 기본값이
 *    true라, 아무것도 입력하지 않은 장소도 이미 "찜"으로 채워진 것처럼 보인다.
 *    하트 토글은 상세 화면에서 별도의 즉각 반응(팝 애니메이션)으로만 다룬다.
 *  · STORY는 장소 메모와 방문 기록의 메모/감정을 함께 본다. 사용자 입장에서 둘 다
 *    "이 장소에 대해 내가 남긴 이야기"이고, 어디에 적었는지는 중요하지 않다.
 *  · 빈 문자열은 채워진 것으로 치지 않는다 — 공백만 있는 태그/메모로 화면이
 *    부풀어 오르면 그 반응이 거짓말이 된다.
 */
fun calculatePlaceRichness(place: Place, visits: List<Visit>): PlaceRichness {
    val hasStory = !place.note.isNullOrBlank() ||
        visits.any { !it.note.isNullOrBlank() || !it.emotion.isNullOrBlank() }

    val signals = buildSet {
        if (visits.isNotEmpty()) add(RichnessSignal.VISIT)
        if (!place.imageFileName.isNullOrBlank()) add(RichnessSignal.PHOTO)
        if (place.tags.any { it.isNotBlank() }) add(RichnessSignal.TAGS)
        if (hasStory) add(RichnessSignal.STORY)
    }

    return PlaceRichness(
        signals = signals,
        isRegular = visits.size >= REGULAR_VISIT_THRESHOLD,
    )
}
